/**
 * CosmicOnboarding Component
 *
 * Step 1 — Birth details form (name, gender, dob, tob, place)
 * Step 2 — Phone / Email OTP verification (inline, no navigation)
 *
 * On OTP success the new account token is persisted to localStorage,
 * birth data is synced to the account, then onComplete(formData) is
 * called to proceed to the dashboard as a verified user.
 */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Sparkles, User, Users, MapPin, LogIn, Phone, Mail, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import ImprovedDateTimePicker from './ui/ImprovedDateTimePicker';
import { searchPlaces, getBackendUrl } from '../services/apiClient';
import LegalFooter from './LegalFooter';
import { trackEvent } from '../utils/analytics';
import { clientHeaders, guestHeaders } from '../utils/clientHeaders';
import { getFbp } from '../lib/metaPixel';
import { useAuth } from '../contexts/AuthContext';
import BirthPlaceHint from './BirthPlaceHint';
import useBirthPlaceField from '../hooks/useBirthPlaceField';
import {
  isPlaceUnresolved, isPlaceCheckBusy, classifySaveFailure, saveFailureMessage,
} from '../utils/birthPlace';

// ── Helpers ──────────────────────────────────────────────────────────────────

const Input = ({ className, ...props }) => (
  <input
    className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
    {...props}
  />
);

// Six-box OTP input (same design as LoginPage)
function OtpBoxes({ value, onChange, disabled }) {
  const refs = useRef([]);

  function handleChange(idx, char) {
    const clean = (char || '').replace(/\D/g, '').slice(-1);
    const next = [...value];
    next[idx] = clean;
    onChange(next);
    if (clean && idx < 5) refs.current[idx + 1]?.focus();
  }

  function handleKeyDown(idx, e) {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  }

  return (
    <div className="flex justify-between gap-2" data-testid="otp-verify-boxes">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={el => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          disabled={disabled}
          data-testid={`otp-verify-box-${i}`}
          style={{
            width: '44px',
            height: '52px',
            background: 'rgba(255,255,255,0.05)',
            border: `1px solid ${value[i] ? 'rgba(245,158,11,0.7)' : 'rgba(245,158,11,0.25)'}`,
            borderRadius: '12px',
            color: '#fff',
            textAlign: 'center',
            fontSize: '22px',
            fontWeight: 'bold',
            outline: 'none',
            transition: 'border-color 0.2s',
          }}
        />
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const CosmicOnboarding = ({ onComplete, isAuthenticated = false }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const auth = useAuth();

  // ── Step & form state ──────────────────────────────────────────────────────
  const [step, setStep] = useState('form'); // 'form' | 'verify'
  const [pendingFormData, setPendingFormData] = useState(null);

  const [formData, setFormData] = useState({
    name: '', gender: '', date: '', time: '',
  });
  // Birth place lives in its own state: typing clears coordinates, and submit
  // stays disabled until a suggestion is picked or /api/geo/resolve finds it.
  const birthPlace = useBirthPlaceField();
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingPlaces, setSearchingPlaces] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Verify step state ──────────────────────────────────────────────────────
  const [verifyMode, setVerifyMode] = useState('phone'); // 'phone' | 'email'
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [inputError, setInputError] = useState('');
  const [otpError, setOtpError] = useState('');

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Cleanup place search timeout
  useEffect(() => {
    return () => { if (searchTimeout) clearTimeout(searchTimeout); };
  }, [searchTimeout]);

  // ── Form handlers ──────────────────────────────────────────────────────────

  const handleInputChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePlaceChange = async e => {
    const value = e.target.value;
    birthPlace.onType(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    if (value.length >= 2) {
      const t = setTimeout(async () => {
        setSearchingPlaces(true);
        try {
          const data = await searchPlaces(value);
          const suggestions = data.suggestions || [];
          setPlaceSuggestions(suggestions);
          setShowSuggestions(suggestions.length > 0);
        } catch {
          setPlaceSuggestions([]);
          setShowSuggestions(false);
        } finally {
          setSearchingPlaces(false);
        }
      }, 300);
      setSearchTimeout(t);
    } else {
      setPlaceSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePlaceSelect = suggestion => {
    const placeName = suggestion.name || suggestion.full_name;
    birthPlace.onPick(suggestion);
    setShowSuggestions(false);
    setPlaceSuggestions([]);
    trackEvent('birth_place_selected', { place: placeName, has_coordinates: true });
  };

  // A failed save came back from onComplete (App already showed the toast):
  // keep the form, and put the place field in the matching state.
  const applySaveFailure = (failure) => {
    if (failure?.placeCheckBusy) birthPlace.markCheckFailed(); // "tap to retry"
    else if (failure?.placeUnresolved) birthPlace.markUnresolved();
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error('Please enter your name'); return; }
    if (!formData.gender.trim()) { toast.error('Please select your gender'); return; }
    if (!formData.date || !formData.time) { toast.error('Please enter your birth date and time'); return; }
    if (!birthPlace.place.trim()) { toast.error('Please enter your birth place'); return; }
    if (!birthPlace.ready) return; // the inline hint under the field says what to do
    const fullData = {
      ...formData,
      place: birthPlace.place,
      latitude: birthPlace.latitude,
      longitude: birthPlace.longitude,
    };

    // Already authenticated — skip OTP and proceed directly
    if (isAuthenticated) {
      setIsSubmitting(true);
      try {
        const result = await onComplete(fullData);
        // onboarding_completed is server-owned (Analytics T2): the backend emits
        // it from the onboarding/birth-details endpoint. The client must not emit it.
        if (result?.saveFailed) applySaveFailure(result); // form stays open
      } catch (err) {
        const failure = classifySaveFailure(err);
        applySaveFailure(failure);
        toast.error(failure.placeCheckBusy || failure.placeUnresolved
          ? saveFailureMessage(failure)
          : 'Failed to process your details. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Guest — save form data and show verification step
    sessionStorage.setItem('guestBirthData', JSON.stringify(fullData));
    setPendingFormData(fullData);
    setStep('verify');
  };

  // ── OTP handlers ───────────────────────────────────────────────────────────

  const sendOtp = async () => {
    setInputError('');
    const API = getBackendUrl();

    if (verifyMode === 'phone') {
      const p = phone.trim();
      if (!/^\d{10}$/.test(p)) { setInputError('Enter a valid 10-digit Indian mobile number.'); return; }
      setSendingOtp(true);
      try {
        const res = await fetch(`${API}/api/auth/request-phone-otp`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: '+91' + p }),
        });
        const data = await res.json();
        if (!res.ok) { setInputError(data.detail || 'Unable to send OTP. Please try again.'); return; }
        setOtp(['', '', '', '', '', '']);
        setOtpSent(true);
        setCountdown(30);
      } catch { setInputError('Network error. Please try again.'); }
      finally { setSendingOtp(false); }
    } else {
      const e = email.trim().toLowerCase();
      if (!/^\S+@\S+\.\S+$/.test(e)) { setInputError('Enter a valid email address.'); return; }
      setSendingOtp(true);
      try {
        const res = await fetch(`${API}/api/auth/request-otp`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: e }),
        });
        const data = await res.json();
        if (!res.ok) { setInputError(data.detail || 'Unable to send OTP. Please try again.'); return; }
        setOtp(['', '', '', '', '', '']);
        setOtpSent(true);
        setCountdown(30);
      } catch { setInputError('Network error. Please try again.'); }
      finally { setSendingOtp(false); }
    }
  };

  const verifyOtp = async otpString => {
    if (verifyingOtp) return;
    setOtpError('');
    const API = getBackendUrl();
    setVerifyingOtp(true);
    try {
      const endpoint = verifyMode === 'phone'
        ? `${API}/api/auth/login-phone-otp`
        : `${API}/api/auth/login-otp`;
      // Carry the pre-auth guest id so the backend folds that doc in place
      // rather than orphaning it behind the new account (header + body field).
      const guestId = localStorage.getItem('guest_user_id') || undefined;
      const guestBody = guestId && guestId.startsWith('guest_') ? { guest_user_id: guestId } : {};
      const body = verifyMode === 'phone'
        ? { phone: '+91' + phone.trim(), otp: otpString, fbp: getFbp(), ...guestBody }
        : { email: email.trim().toLowerCase(), otp: otpString, fbp: getFbp(), ...guestBody };

      const res = await fetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...clientHeaders(), ...guestHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setOtpError(data.detail || 'Incorrect OTP. Please try again.'); return; }

      if (data.is_new_user) {
        localStorage.setItem('is_new_registration', 'true');
      }

      await handleAuthSuccess(data.session_token, data.user);
    } catch { setOtpError('Network error. Please try again.'); }
    finally { setVerifyingOtp(false); }
  };

  const handleOtpChange = nextOtp => {
    setOtp(nextOtp);
    setOtpError('');
    const joined = nextOtp.join('');
    if (joined.length === 6 && !nextOtp.includes('')) verifyOtp(joined);
  };

  const handleAuthSuccess = async (token, user) => {
    const API = getBackendUrl();

    // Persist session to localStorage so App.js & TrialWelcomeModal can read it
    localStorage.setItem('jyotish_session_token', token);
    localStorage.setItem('jyotish_auth_user', JSON.stringify(user));

    // CRITICAL (JP-118): push the session into AuthContext NOW. Without this,
    // AuthContext stays user=null for the rest of this SPA session, so the
    // onComplete handlers below saw a STALE isAuthenticated=false and forked
    // into the guest branch — minting an orphan guest_ doc AFTER registration
    // and running the dashboard / ChitraGupt gate as the guest. Updating the
    // context here (setUser + justAuthenticated) keeps the whole app coherent.
    try { await auth?.handleAuthCallback?.(token, user); } catch { /* non-fatal */ }

    // Sync birth data to the new account. The backend merges the guest doc
    // even when it cannot map the place, then answers 422 place_unresolved —
    // in that case go back to the form (now signed in) to pick the place.
    let syncFailure = null; // place_unresolved (422) or place_check_busy (503)
    try {
      const syncRes = await fetch(`${API}/api/auth/sync-guest-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...clientHeaders(),
        },
        body: JSON.stringify({
          name: pendingFormData.name,
          gender: pendingFormData.gender,
          date: pendingFormData.date,
          time: pendingFormData.time,
          place: pendingFormData.place,
          latitude: pendingFormData.latitude,
          longitude: pendingFormData.longitude,
          guest_user_id: localStorage.getItem('guest_user_id') || undefined,
        }),
      });
      localStorage.removeItem('guest_user_id');
      if (!syncRes.ok && (syncRes.status === 422 || syncRes.status === 503)) {
        const body = await syncRes.json().catch(() => ({}));
        if (isPlaceUnresolved(body) || isPlaceCheckBusy(body)) syncFailure = classifySaveFailure(body);
      }
      if (!syncFailure) sessionStorage.removeItem('guestBirthData');
    } catch {
      // Non-critical — birth data saved via onComplete flow below
    }
    if (syncFailure) {
      applySaveFailure(syncFailure);
      setStep('form');
      toast.error(saveFailureMessage(syncFailure));
      return;
    }

    // onboarding_completed is server-owned (Analytics T2): the backend emits it
    // from the onboarding/birth-details endpoint. The client must not emit it.

    setIsSubmitting(true);
    try {
      const result = await onComplete(pendingFormData);
      if (result?.saveFailed) { // App already showed the toast
        applySaveFailure(result);
        setStep('form');
      }
    } catch {
      toast.error('Failed to load your chart. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchVerifyMode = mode => {
    setVerifyMode(mode);
    setOtpSent(false);
    setOtp(['', '', '', '', '', '']);
    setInputError('');
    setOtpError('');
    setCountdown(0);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="cosmic-onboarding">
      <div className="cosmic-background">
        <div className="stars-layer-1" />
        <div className="stars-layer-2" />
        <div className="cosmic-gradient" />
      </div>

      {/* Top Login Button — hidden when already authenticated */}
      {!isAuthenticated && (
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="fixed top-4 right-4 z-50"
        >
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500/90 hover:bg-amber-600 text-white rounded-full font-medium text-sm shadow-lg transition-all hover:scale-105"
            data-testid="header-login-btn"
          >
            <LogIn className="w-4 h-4" />
            <span>Login</span>
          </button>
        </motion.div>
      )}

      <div className="onboarding-container single-screen">
        <AnimatePresence mode="wait">

          {/* ── STEP 1: Birth details form ───────────────────────────────── */}
          {step === 'form' && (
            <motion.div
              key="form"
              initial={{ opacity: 1, y: 0 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="welcome-section compact"
            >
              <motion.div
                initial={{ scale: 1, opacity: 1 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.15 }}
                className="app-branding compact flex flex-col items-center justify-center text-center"
              >
                <div className="w-20 h-20 rounded-xl overflow-hidden mb-3 mx-auto">
                  <img src="/logo-mobile.png" alt="JyotishPuraan" className="w-full h-full object-cover" />
                </div>
                <h1 className="app-title-cosmic small">JyotishPuraan</h1>
                <p className="app-subtitle-cosmic small">AI-Powered Astrology ✨</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 1, y: 0 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
                className="form-cosmic single-form"
              >
                {/* Name */}
                <div className="form-field">
                  <label className="field-label">
                    <User className="w-4 h-4 inline mr-2 text-amber-400" />
                    Your Name
                  </label>
                  <Input
                    type="text" name="name" value={formData.name}
                    onChange={handleInputChange} placeholder="Enter your name"
                    className="cosmic-input compact" autoFocus
                    data-testid="onboarding-name-input"
                  />
                </div>

                {/* Gender */}
                <div className="form-field">
                  <label className="field-label">
                    <Users className="w-4 h-4 inline mr-2 text-amber-400" />
                    Gender
                  </label>
                  <div className="gender-buttons">
                    <button type="button" data-testid="gender-male-btn"
                      className={`gender-btn ${formData.gender === 'Male' ? 'selected' : ''}`}
                      onClick={() => { setFormData({ ...formData, gender: 'Male' }); trackEvent('gender_toggle_clicked', { selection: 'Male' }); }}
                    >
                      <span className="gender-icon">👨🏻</span><span>Male</span>
                    </button>
                    <button type="button" data-testid="gender-female-btn"
                      className={`gender-btn ${formData.gender === 'Female' ? 'selected' : ''}`}
                      onClick={() => { setFormData({ ...formData, gender: 'Female' }); trackEvent('gender_toggle_clicked', { selection: 'Female' }); }}
                    >
                      <span className="gender-icon">👩🏻</span><span>Female</span>
                    </button>
                  </div>
                </div>

                {/* Date & Time */}
                <div className="form-field datetime-picker-wrapper">
                  <ImprovedDateTimePicker
                    onDateChange={date => { setFormData({ ...formData, date }); if (date) trackEvent('birth_date_selected', { date }); }}
                    onTimeChange={time => { setFormData({ ...formData, time }); if (time) trackEvent('birth_time_selected', { time }); }}
                    initialDate={formData.date} initialTime={formData.time}
                  />
                </div>

                {/* Place */}
                <div className="form-field">
                  <label className="field-label">
                    <MapPin className="w-4 h-4 inline mr-2 text-amber-400" />
                    Place of Birth
                  </label>
                  <div className="place-input-wrapper" style={{ position: 'relative' }}>
                    <Input
                      type="text" name="place" value={birthPlace.place}
                      onChange={handlePlaceChange} placeholder="City, State, Country"
                      // Delay so a click on a suggestion lands first.
                      onBlur={() => setTimeout(() => birthPlace.resolveTyped(), 200)}
                      className="cosmic-input compact" autoComplete="off"
                      data-testid="onboarding-place-input"
                    />
                    {searchingPlaces && (
                      <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-amber-400" />
                    )}
                    {showSuggestions && placeSuggestions.length > 0 && (
                      <div
                        data-testid="place-suggestions"
                        style={{
                          position: 'absolute', top: '100%', left: 0, right: 0,
                          background: 'linear-gradient(135deg, #1e1e32 0%, #2d1f4e 100%)',
                          border: '1px solid rgba(255,255,255,0.3)', borderRadius: '12px',
                          marginTop: '4px', maxHeight: '200px', overflowY: 'auto',
                          zIndex: 9999, boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                        }}
                      >
                        {placeSuggestions.slice(0, 5).map((s, i) => (
                          <button
                            key={i} type="button"
                            onClick={() => handlePlaceSelect(s)}
                            data-testid={`place-suggestion-${i}`}
                            style={{
                              display: 'flex', alignItems: 'center', width: '100%',
                              padding: '12px 16px', background: 'transparent', border: 'none',
                              borderBottom: i < placeSuggestions.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                              textAlign: 'left', color: '#ffffff', fontSize: '14px', cursor: 'pointer',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                          >
                            <MapPin className="w-4 h-4 mr-3 text-amber-400" style={{ flexShrink: 0 }} />
                            <span>{s.label || s.name || s.full_name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <BirthPlaceHint status={birthPlace.status} place={birthPlace.place} matchedName={birthPlace.matchedName} onRetry={birthPlace.resolveTyped} />
                </div>

                {/* Submit */}
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit} disabled={isSubmitting || !birthPlace.ready}
                  className="submit-btn-cosmic" data-testid="submit-btn-cosmic"
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Preparing Your Journey...</>
                  ) : (
                    <><Sparkles className="w-5 h-5 mr-2" />Start My Cosmic Journey</>
                  )}
                </motion.button>
              </motion.div>
            </motion.div>
          )}

          {/* ── STEP 2: Phone / Email OTP verification ───────────────────── */}
          {step === 'verify' && (
            <motion.div
              key="verify"
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
              className="welcome-section compact"
              data-testid="verify-step"
            >
              {/* Back button */}
              <button
                onClick={() => { setStep('form'); setOtpSent(false); setInputError(''); setOtpError(''); }}
                data-testid="verify-back-btn"
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'transparent', border: 'none',
                  color: 'rgba(255,255,255,0.5)', fontSize: '14px',
                  cursor: 'pointer', marginBottom: '20px', padding: '4px 0',
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              {/* Branding */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 rounded-xl overflow-hidden mb-3 mx-auto">
                  <img src="/logo-mobile.png" alt="JyotishPuraan" className="w-full h-full object-cover" />
                </div>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', fontWeight: 700, color: '#fbbf24', marginBottom: '6px' }}>
                  Verify to get your free trial
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', lineHeight: 1.5 }}>
                  Create your account to unlock 599 Karma points — experience a ChitraGupt session
                </p>
              </div>

              {/* Mode toggle */}
              <div
                style={{
                  display: 'flex', background: 'rgba(255,255,255,0.05)',
                  borderRadius: '14px', padding: '4px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  marginBottom: '20px',
                }}
              >
                {[{ key: 'phone', label: 'Mobile', Icon: Phone }, { key: 'email', label: 'Email', Icon: Mail }].map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={() => switchVerifyMode(key)}
                    data-testid={`verify-mode-${key}`}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: '6px', padding: '10px 0', borderRadius: '10px',
                      border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                      transition: 'all 0.2s',
                      background: verifyMode === key ? 'rgba(245,158,11,0.15)' : 'transparent',
                      color: verifyMode === key ? '#fbbf24' : 'rgba(255,255,255,0.4)',
                      boxShadow: verifyMode === key ? '0 0 0 1px rgba(245,158,11,0.3)' : 'none',
                    }}
                  >
                    <Icon style={{ width: '14px', height: '14px' }} />
                    {label}
                  </button>
                ))}
              </div>

              {/* Input field */}
              {!otpSent ? (
                <div style={{ marginBottom: '16px' }}>
                  {verifyMode === 'phone' ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '10px', padding: '0 12px',
                        color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}>
                        +91
                      </div>
                      <input
                        type="tel" inputMode="numeric" maxLength={10}
                        value={phone}
                        onChange={e => { setPhone(e.target.value.replace(/\D/g, '')); setInputError(''); }}
                        placeholder="10-digit mobile number"
                        data-testid="verify-phone-input"
                        style={{
                          flex: 1, padding: '12px 14px',
                          background: 'rgba(255,255,255,0.06)',
                          border: `1px solid ${inputError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.15)'}`,
                          borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none',
                        }}
                        onFocus={e => (e.target.style.borderColor = 'rgba(245,158,11,0.5)')}
                        onBlur={e => (e.target.style.borderColor = inputError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.15)')}
                      />
                    </div>
                  ) : (
                    <input
                      type="email" inputMode="email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); setInputError(''); }}
                      placeholder="your@email.com"
                      data-testid="verify-email-input"
                      style={{
                        width: '100%', padding: '12px 14px',
                        background: 'rgba(255,255,255,0.06)',
                        border: `1px solid ${inputError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.15)'}`,
                        borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none',
                        boxSizing: 'border-box',
                      }}
                      onFocus={e => (e.target.style.borderColor = 'rgba(245,158,11,0.5)')}
                      onBlur={e => (e.target.style.borderColor = inputError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.15)')}
                    />
                  )}

                  {inputError && (
                    <p data-testid="verify-input-error" style={{ color: '#f87171', fontSize: '12px', marginTop: '6px' }}>
                      {inputError}
                    </p>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={sendOtp} disabled={sendingOtp}
                    data-testid="send-otp-btn"
                    style={{
                      width: '100%', marginTop: '14px', padding: '13px',
                      background: sendingOtp ? 'rgba(245,158,11,0.4)' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                      border: 'none', borderRadius: '12px',
                      color: '#fff', fontSize: '15px', fontWeight: 600,
                      cursor: sendingOtp ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}
                  >
                    {sendingOtp ? (
                      <><Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />Sending OTP...</>
                    ) : (
                      `Send OTP via ${verifyMode === 'phone' ? 'SMS' : 'Email'}`
                    )}
                  </motion.button>
                </div>
              ) : (
                /* OTP entry */
                <div style={{ marginBottom: '8px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px', marginBottom: '14px', textAlign: 'center' }}>
                    OTP sent to{' '}
                    <span style={{ color: '#fbbf24', fontWeight: 600 }}>
                      {verifyMode === 'phone' ? `+91 ${phone}` : email}
                    </span>
                  </p>

                  <OtpBoxes value={otp} onChange={handleOtpChange} disabled={verifyingOtp || isSubmitting} />

                  {otpError && (
                    <p data-testid="verify-otp-error" style={{ color: '#f87171', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                      {otpError}
                    </p>
                  )}

                  {(verifyingOtp || isSubmitting) && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '14px', color: '#fbbf24', fontSize: '13px' }}>
                      <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                      {isSubmitting ? 'Loading your chart...' : 'Verifying...'}
                    </div>
                  )}

                  {/* Resend */}
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    {countdown > 0 ? (
                      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>
                        Resend in <span style={{ color: '#fbbf24' }}>{countdown}s</span>
                      </p>
                    ) : (
                      <button
                        onClick={sendOtp} disabled={sendingOtp}
                        data-testid="resend-otp-btn"
                        style={{
                          background: 'transparent', border: 'none',
                          color: '#fbbf24', fontSize: '13px',
                          cursor: 'pointer', textDecoration: 'underline', padding: 0,
                        }}
                      >
                        {sendingOtp ? 'Sending...' : 'Resend OTP'}
                      </button>
                    )}
                  </div>

                  {/* Change number/email */}
                  <div style={{ textAlign: 'center', marginTop: '8px' }}>
                    <button
                      onClick={() => { setOtpSent(false); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
                      data-testid="change-contact-btn"
                      style={{
                        background: 'transparent', border: 'none',
                        color: 'rgba(255,255,255,0.35)', fontSize: '12px',
                        cursor: 'pointer', padding: 0,
                      }}
                    >
                      Change {verifyMode === 'phone' ? 'mobile number' : 'email'}
                    </button>
                  </div>
                </div>
              )}

              {/* Legal note */}
              <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '11px', textAlign: 'center', marginTop: '20px', lineHeight: 1.6 }}>
                By continuing, you agree to our Terms of Service and Privacy Policy.
                Your number is only used for account verification.
              </p>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      <LegalFooter />
    </div>
  );
};

export default CosmicOnboarding;
